<template>
  <full-screen :title="t('close-delivery.title')" @close="router.back()">
    <div class="max-w-[33rem] px-4 m-auto mt-4">
      <div v-if="hasEnabledServices">
        <delivery-selector
          :virtual-brand-deliveries="virtualBrandsClosingTimes"
          :current-time="currentTime"
          show-closed-until
          @close-delivery="(brandId, company) => setDeliveryToClose(brandId, company)"
          @open-delivery="(brandId, company) => openDelivery(brandId, company)"
          @close-brand="setBrandToClose"
          @open-brand="openBrand"
        />
        <div class="border-t border-n-700">
          <div class="flex flex-row items-center">
            <div class="flex-1">
              <l-button
                type="text"
                class="pl-0 !text-n-0 !hover:text-v-600"
                @click="() => (mainState == 1 ? setAllDeliveriesToClose() : openAllDeliveries())"
              >
                {{ mainState == 1 ? t('close-delivery.close-all') : t('close-delivery.open-all') }}
              </l-button>
            </div>
            <div class="flex-none">
              <l-switch v-model="mainState" :enable-on-partial-state="true" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mt-12 mb-4 text-n-200 text-center">
        <l-icon name="bike" class="mx-auto w-10 h-10" />
        <span class="inline-block my-2 uppercase heading-3">
          {{ t('close-delivery.no-services-message') }}
        </span>
      </div>
    </div>
    <closing-time-modal
      v-if="closingBrandsSelected"
      @close="closingBrandsSelected = false"
      @confirm="startClosing"
    />
  </full-screen>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { differenceInDays, formatDistance, parseISO } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LIcon, LSwitch } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import { useNotifications } from '@/composables/useNotifications'
import ClosingTimeModal from '@/modules/delivery/ClosingTimeModal.vue'
import DeliverySelector from '@/modules/delivery/DeliverySelector.vue'
import { useDeliveryCompaniesStore } from '@/store/deliveryCompanies'
import type { ClosingTime } from '@/types/virtualBrands'

const router = useRouter()
const { notifySuccess } = useNotifications()
const { t } = useI18n()

const deliveryCompaniesStore = useDeliveryCompaniesStore()

const { virtualBrandsClosingTimes } = storeToRefs(deliveryCompaniesStore)
const { closeDeliveries, openDeliveries } = deliveryCompaniesStore

const selectedBrandAndCompanies = ref<Record<string, string[]>>({})
const closingBrandsSelected = ref(false)

const currentTime = useNow({
  interval: 1000
})

const totalEnabledServices = computed(() => {
  return Object.values(virtualBrandsClosingTimes.value)
    .map(vb => Object.keys(vb.externalDeliveryCompaniesClosingTimes).length)
    .reduce((total, count) => total + count, 0)
})

const hasEnabledServices = computed(() => {
  return totalEnabledServices.value > 0
})

type SwitchModelType = boolean | 0 | 1 | 2
const mainState = computed({
  get: (): SwitchModelType => {
    let generalState: SwitchModelType | undefined
    Object.values(virtualBrandsClosingTimes.value).forEach(brand => {
      const length = Object.values(brand.externalDeliveryCompaniesClosingTimes).length
      const times = Object.values(brand.externalDeliveryCompaniesClosingTimes).filter(time => {
        return time
      })

      const brandValue = times.length === 0 ? 1 : times.length === length ? 0 : 2
      if (typeof generalState === 'undefined') {
        generalState = brandValue
      } else if (generalState !== brandValue) {
        generalState = 2
      }
    })

    return generalState || 0
  },
  set: (val: boolean) => {
    if (val) {
      openAllDeliveries()
    } else {
      setAllDeliveriesToClose()
    }
  }
})

function setAllDeliveriesToClose() {
  virtualBrandsClosingTimes.value.forEach(brand => {
    selectedBrandAndCompanies.value[brand.id] = Object.keys(
      brand.externalDeliveryCompaniesClosingTimes
    )
  })
  closingBrandsSelected.value = true
}

function setBrandToClose(brandId: string) {
  selectedBrandAndCompanies.value[brandId] = Object.keys(
    virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)!
      .externalDeliveryCompaniesClosingTimes
  )
  closingBrandsSelected.value = true
}

function openAllDeliveries(): void {
  const deliveriesToOpen: Record<string, string[]> = {}
  Object.values(virtualBrandsClosingTimes.value).forEach(brand => {
    deliveriesToOpen[brand.id] = Object.keys(
      virtualBrandsClosingTimes.value.find(b => b.id === brand.id)!
        .externalDeliveryCompaniesClosingTimes
    )
  })

  openDeliveries(deliveriesToOpen)

  notifySuccess({
    title: t('open-delivery.open-all'),
    onAction: () => {
      router.push({ name: 'closeDelivery' })
    }
  })
}

function openBrand(brandId: string) {
  const deliveriesToOpen: Record<string, string[]> = {}
  deliveriesToOpen[brandId] = Object.keys(
    virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)!
      .externalDeliveryCompaniesClosingTimes
  )
  openDeliveries(deliveriesToOpen)
  const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
  const brandName = brand ? brand.brandName : ''

  notifySuccess({
    title: `${t('open-delivery.open-brand')} ${brandName}`,
    onAction: () => {
      router.push({ name: 'closeDelivery' })
    }
  })
}

function setDeliveryToClose(brandId: string, company: string) {
  selectedBrandAndCompanies.value[brandId] = [company]
  closingBrandsSelected.value = true
}

function openDelivery(brandId: string, company: string) {
  openDeliveries({
    [brandId]: [company]
  })

  const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
  const brandName = brand ? brand.brandName : ''

  notifySuccess({
    title: `${t('open-delivery.open-delivery')} ${company} ${brandName ? `(${brandName})` : ''}`,
    onAction: () => {
      router.push({ name: 'closeDelivery' })
    }
  })
}

function startClosing(closingTime: ClosingTime) {
  const closedUntil = closeDeliveries(selectedBrandAndCompanies.value, closingTime)

  showClosingNotifications(parseISO(closedUntil))

  selectedBrandAndCompanies.value = {}

  closingBrandsSelected.value = false
}

function showClosingNotifications(closedUntil: Date): void {
  const values = Object.values(selectedBrandAndCompanies.value)
  if (values.length > 1) {
    notifySuccess({
      title: t('close-delivery.delivery-closed'),
      description: timeFrom(closedUntil),
      onAction: () => {
        router.push({ name: 'closeDelivery' })
      }
    })
  } else if (values.length === 1 && values[0].length > 1) {
    const brandId = Object.keys(selectedBrandAndCompanies.value)[0]
    const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
    const brandName = brand ? brand.brandName : ''

    notifySuccess({
      title: `${t('close-delivery.close-brand')} ${brandName}`,
      description: timeFrom(new Date(closedUntil)),
      onAction: () => {
        router.push({ name: 'closeDelivery' })
      }
    })
  } else {
    const brandId = Object.keys(selectedBrandAndCompanies.value)[0]
    const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
    const brandName = brand ? brand.brandName : ''

    notifySuccess({
      title: `${t('close-delivery.close-delivery')}  ${values[0]} (${brandName})`,
      description: timeFrom(new Date(closedUntil)),
      onAction: () => {
        router.push({ name: 'closeDelivery' })
      }
    })
  }
}

function timeFrom(date: Date) {
  if (differenceInDays(date, currentTime.value) > 365) {
    return t('close-delivery.indefinitely')
  } else {
    return formatDistance(date, currentTime.value)
  }
}
</script>
